// Pinch maths for the document viewer, kept free of React and the DOM so it can
// be unit tested. Coordinates are CSS pixels; anchors are in the unscaled
// coordinate space of the rendered pages.

export const MIN_SCALE = 1;
export const MAX_SCALE = 4;

export const clampScale = (scale) => {
    if (!isFinite(scale)) {
        return MIN_SCALE;
    }
    return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
};

export const touchDistance = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

const clampScroll = (value, contentExtent, scale, viewportExtent) => {
    if (!contentExtent) {
        // Not measured yet; clamping against a zero extent would pin the top-left.
        return Math.max(0, value);
    }
    return Math.min(Math.max(0, contentExtent * scale - viewportExtent), Math.max(0, value));
};

// The scroll offsets a given scale and finger midpoint resolve to, clamped the
// way the browser will clamp them once the pinch commits.
const scrollFor = (pinch, scale, midX, midY) => ({
    left: clampScroll(pinch.anchorX * scale - midX, pinch.contentWidth, scale, pinch.viewportWidth),
    top: clampScroll(pinch.anchorY * scale - midY, pinch.contentHeight, scale, pinch.viewportHeight),
});

const midpoint = (pinch, touchA, touchB) => ({
    midX: (touchA.clientX + touchB.clientX) / 2 - pinch.containerLeft,
    midY: (touchA.clientY + touchB.clientY) / 2 - pinch.containerTop,
});

export const beginPinch = ({touchA, touchB, containerRect, scrollLeft, scrollTop, scale, viewport, content}) => {
    const pinch = {
        startDistance: touchDistance(touchA, touchB),
        startScale: scale,
        scale,
        containerLeft: containerRect.left,
        containerTop: containerRect.top,
        scrollLeft,
        scrollTop,
        viewportWidth: viewport.width,
        viewportHeight: viewport.height,
        contentWidth: content.width,
        contentHeight: content.height,
    };

    const {midX, midY} = midpoint(pinch, touchA, touchB);

    return {
        ...pinch,
        midX,
        midY,
        anchorX: (scrollLeft + midX) / scale,
        anchorY: (scrollTop + midY) / scale,
    };
};

export const pinchTransform = (pinch, touchA, touchB) => {
    const distance = touchDistance(touchA, touchB);
    if (!pinch.startDistance || !distance) {
        return {scale: pinch.scale, midX: pinch.midX, midY: pinch.midY, tx: 0, ty: 0};
    }

    const {midX, midY} = midpoint(pinch, touchA, touchB);
    const scale = clampScale(pinch.startScale * (distance / pinch.startDistance));
    const target = scrollFor(pinch, scale, midX, midY);

    // The container cannot scroll while the fingers are down, so the translation
    // carries the difference: drawing at the offsets the pinch will commit to
    // makes the live frame identical to the committed one, clamping included.
    return {
        scale,
        midX,
        midY,
        tx: pinch.scrollLeft - target.left,
        ty: pinch.scrollTop - target.top,
    };
};

export const anchorScroll = (pinch, scale) => scrollFor(pinch, scale, pinch.midX, pinch.midY);
